'use client'
// Types
import type { IOption } from '@/CommonComponents/Autocomplete'
// Components
import { Autocomplete, Box, Typography } from '@/CommonComponents'
// Hooks
import { useMemo } from 'react'

interface IAutocompleteFilterProps<T> {
  data: T[]
  selectedValue: string | null
  onChange: (value: string | null) => void
  valueExtractor: (item: T) => string | undefined
  placeholder: string
}

export default function AutocompleteFilter<T>({
  data,
  selectedValue,
  onChange,
  valueExtractor,
  placeholder,
}: IAutocompleteFilterProps<T>) {
  const options: IOption[] = useMemo(() => {
    const uniqueValues = [...new Set(data.map(valueExtractor).filter(Boolean))] as string[]
    return uniqueValues.map((value) => {
      const count = data.filter((item) => valueExtractor(item) === value).length
      return { id: value, label: `${value} (${count})` }
    })
  }, [data, valueExtractor])

  if (options.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
      <Autocomplete
        options={options}
        values={selectedValue || ''}
        placeholder={placeholder}
        onChange={(value) => {
          const selected = Array.isArray(value) ? value[0] : value
          onChange(selected || null)
        }}
        size="small"
        sx={{ minWidth: 300 }}
        renderOption={(props, option) => {
          const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string }
          return (
            <li key={key} {...rest}>
              <Typography variant="body2">{option.label}</Typography>
            </li>
          )
        }}
      />
    </Box>
  )
}
